import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose, COURSE_PROVIDERS } from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Notifications from '../../../services/notifications';
import Loader from '../../../services/loader';
import CustomIframe from './CustomIframe';
import { useScrollAnchor } from '../../../services/customHooks';
import SocketClient from '../../../services/socketClient';
import ImageProp from '../../../commonComponents/propTypes/imageProp';

function CoursePreviewSCORM({ t, _csrf, course }) {
  const loader = useRef(new Loader('#scormContentLayout'));
  const frameId = '#scormFrame';
  const frameIdEl = useRef(null);
  const [showCloseCourse, setShowCloseCourse] = useState(false);
  const closeCourseBtnRef = useRef(null);
  const [isLoading, setLoading] = useState(true);
  const [urlUser, setUrlUser] = useState('');
  const [scormLoaded, setScormLoaded] = useState(false);
  const [socketStatus, setSocketStatus] = useState('Connecting...');

  const { contentElement } = useScrollAnchor();

  const getScormUserLink = () =>
    new Promise((resolve, reject) => {
      axios
        .post(
          `/api/academy/course/scorm/public/preview/${course.token}`,
          {
            redirectUrl: `${window.location.origin}/academy/scorm/${course.token}/completed`,
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch(err => {
          reject(err);
        });
    });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const handleCourseStatusChange = courseProviderStatus => {
    const {
      course: courseId,
      completionStatus,
      successStatus,
      score,
      externalProviderId: scormId,
    } = courseProviderStatus;
    console.log('### Socket.io | SCORM Status: ', {
      completionStatus,
      successStatus,
      score,
      courseId,
      scormId,
    });
    if (courseProviderStatus.course === course._id) {
      if (courseProviderStatus.completionStatus === 'completed') {
        if (!course.isManualComplete) {
          setUrlUser(
            `${window.location.origin}/academy/${course.slug}/completed`,
          );
        } else {
          setShowCloseCourse(true);
          closeCourseBtnRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleSocketStatusConnect = () => {
    setSocketStatus('Connected!');
  };

  const handleSocketStatusError = () => {
    setSocketStatus('Error Connection!');
  };

  useEffect(() => {
    getScormUserLink()
      .then(providerURL => {
        setLoading(false);
        setUrlUser(providerURL);
      })
      .catch(err => {
        setLoading(false);
        console.error(err);
        Notifications.showErrorFromRequest(err, 'error_fetchCourse');
      });
    console.log('$$$$$$$ Socket.io testing....');
    SocketClient.on('connect', handleSocketStatusConnect);
    SocketClient.on('connect_error', handleSocketStatusError);
    SocketClient.on('COURSE_SCORM_PREVIEW', handleCourseStatusChange);
  }, [course]);

  const handleCloseCourse = useCallback(() => {
    setUrlUser(`${window.location.origin}/academy/${course.slug}/completed`);
    setShowCloseCourse(false);
  }, [setUrlUser, setShowCloseCourse]);

  const handleClickLoadCourse = () => {
    setScormLoaded(true);
  };

  const handleClickFullScreen = () => {
    if (frameIdEl) {
      frameIdEl.current.requestFullScreenMode();
    }
  };

  const handleSocketStatusColor = status => {
    if (status === 'Connected!') {
      return 'green';
    }
    if (status === 'Error Connection!') {
      return 'red';
    }
    return 'blue';
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        backgroundColor: 'white',
      }}
    >
      <div
        id="scormContentLayout"
        ref={contentElement}
        style={{
          position: 'relative',
          width: '80%',
          height: '80%',
        }}
      >
        <div
          className="scorm-content"
          style={{
            position: 'relative',
            width: '100%',
            height: '700px',
          }}
        >
          {!isLoading && scormLoaded && (
            <>
              <CustomIframe
                id={frameId}
                ref={frameIdEl}
                title={course.name}
                allow="microphone; camera"
                style={{
                  order: 0,
                  height: '100%',
                  width: '100%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
                src={urlUser}
                allowFullScreen
                frameBorder="0"
                onReloadPage={() => {
                  setTimeout(() => {
                    window.location.reload();
                  }, 5000);
                }}
              />
              <div className="buttonFullScreen" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="btn m-btn--pill btn-outline-dynamic"
                  onClick={handleClickFullScreen}
                >
                  <i className="fa fa-expand" />
                  {t('academy.detail.scorm.fullscreen')}
                </button>
                {showCloseCourse && !urlUser.includes('/completed') ? (
                  <button
                    ref={closeCourseBtnRef}
                    type="button"
                    className="btn m-btn--pill btn-dynamic"
                    onClick={handleCloseCourse}
                  >
                    <i className="fa fa-check" />
                    {t('academy.scorm.btn.close')}
                  </button>
                ) : null}
              </div>
            </>
          )}
          {!isLoading && !scormLoaded && (
            <div
              className="enroll-overlay"
              style={{
                position: 'relative',
                width: '100%',
                height: '700px',
              }}
            >
              <h2 className="enroll-title">
                {t('academy.detail.scorm.title_start')}
              </h2>
              <p className="enroll-message">
                {t('academy.detail.scorm.message')}
              </p>
              <button
                type="button"
                className="btn m-btn--pill btn-dynamic"
                onClick={handleClickLoadCourse}
              >
                {t('academy.detail.scorm.button')}
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          marginTop: 16,
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '80%',
        }}
      >
        <span
          style={{
            fontSize: 14,
            color: '#222',
            marginRight: 8,
            fontWeight: 500,
          }}
        >
          Socket Connection Status:
        </span>
        <span
          style={{
            fontSize: 14,
            color: handleSocketStatusColor(socketStatus),
          }}
        >
          {socketStatus}
        </span>
      </div>
    </div>
  );
}

CoursePreviewSCORM.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  course: PropTypes.shape({
    _id: PropTypes.string,
    slug: PropTypes.string,
    name: PropTypes.string,
    summary: PropTypes.string,
    image: PropTypes.oneOfType([
      PropTypes.shape(ImageProp.propType),
      PropTypes.string,
    ]),
    provider: PropTypes.shape({
      service: PropTypes.oneOf(COURSE_PROVIDERS),
      config: PropTypes.shape({
        scorm: PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
          learningStandard: PropTypes.string,
          url: PropTypes.string,
          urlUser: PropTypes.string,
        }),
      }),
    }),
    isManualComplete: PropTypes.bool,
    token: PropTypes.string,
  }).isRequired,
};

CoursePreviewSCORM.defaultProps = {};

export default compose(translate, withCSRF)(CoursePreviewSCORM);
